import { useSelector } from "react-redux";
import I18n from "i18n-js";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { useScale, usePlaySound } from "common/utils";
import { CloseBtn, FadeTransition } from "common/elements";

const Feature = ({
  icon,
  title,
  desc,
}: {
  icon: string;
  title: string;
  desc: string;
}) => (
  <div
    className="flex-row-el items-center gap-4 p-5 rounded-xl bg-white"
    style={{
      boxShadow:
        "-1px 1px 1px rgba(63,74,97,0.10), 1px 1px 3px rgba(63,74,97,0.15)",
    }}
  >
    <span
      className="material-symbols-outlined !text-4xl text-gray2-100"
      aria-hidden="true"
    >
      {icon}
    </span>
    <div className="flex-col-el">
      <p className="text text-textcolor-black font-bold">{title}</p>
      <p className="text text-gray2-100 text-sm mt-1 leading-snug">{desc}</p>
    </div>
  </div>
);

export const SubscriptionPromotion = () => {
  const PANEL_WIDTH = 1180;
  const PANEL_HEIGHT = 750;
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const subscription = useSelector(
    (state: RootState) => state.config.dialog.subscription_promotion,
  );
  const handleClose = () => {
    play();
    handleDialog({ type: DialogType.SUBSCRIPTION_PROMOTION, value: false });
  };

  return (
    <Dialog
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      open={subscription.value}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div
        className="flex-col-el flex-center relative"
        style={{
          filter:
            "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
        }}
      >
        <div
          className="flex-col-view self-center"
          style={{
            width: PANEL_WIDTH * scale,
            height: PANEL_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-view origin-top-left"
            style={{
              width: PANEL_WIDTH,
              height: PANEL_HEIGHT,
              transform: `scale(${scale})`,
            }}
          >
            <CloseBtn close={handleClose} />
            <div className="flex-col-el flex-center w-full h-full rounded-[8px] bg-beige-60 p-6 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white relative overflow-hidden">
              <div className="flex-col-el w-full h-full">
                <div className="flex-col-el flex-center pt-10 pb-4 px-10">
                  <h2 className="text text-textcolor-black !text-[28px] font-bold">
                    {I18n.t("MSG_ACCOUNT_PROMOTION_TITLE")}
                  </h2>
                  <p className="text text-gray2-100 mt-2">
                    {I18n.t("MSG_ACCOUNT_PROMOTION_DESC")}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6 px-10">
                  <Feature
                    icon="sync"
                    title={I18n.t("MSG_ACCOUNT_PROMOTION_SYNC_TITLE")}
                    desc={I18n.t("MSG_ACCOUNT_PROMOTION_SYNC_DESC")}
                  />
                  <Feature
                    icon="group"
                    title={I18n.t("MSG_ACCOUNT_PROMOTION_MULTI_CHILD_TITLE")}
                    desc={I18n.t("MSG_ACCOUNT_PROMOTION_MULTI_CHILD_DESC")}
                  />
                  <Feature
                    icon="folder_special"
                    title={I18n.t("MSG_ACCOUNT_PROMOTION_CREATOR_SLOTS_TITLE")}
                    desc={I18n.t("MSG_ACCOUNT_PROMOTION_CREATOR_SLOTS_DESC")}
                  />
                  <Feature
                    icon="all_inclusive"
                    title={I18n.t("MSG_ACCOUNT_PROMOTION_FREE_COURSE_TITLE")}
                    desc={I18n.t("MSG_ACCOUNT_PROMOTION_FREE_COURSE_DESC")}
                  />
                </div>

                <div className="flex-row-el flex-center gap-6 mt-10 px-10 alpha">
                  <button
                    className="btn btn-blue"
                    onClick={handleClose}
                    aria-label={I18n.t("MSG_ACCOUNT_PROMOTION_LATER_BTN")}
                  >
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span
                        className="material-symbols-outlined !text-4xl"
                        aria-hidden="true"
                      >
                        close
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_ACCOUNT_PROMOTION_LATER_BTN")}
                      </p>
                    </div>
                  </button>

                  <button
                    className="btn btn-primary"
                    aria-label={I18n.t("MSG_ACCOUNT_PROMOTION_SIGNUP_BTN")}
                  >
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span
                        className="material-symbols-outlined !text-4xl"
                        aria-hidden="true"
                      >
                        person_add
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_ACCOUNT_PROMOTION_SIGNUP_BTN")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
